import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Checkbox,
  FormControlLabel,
  GlobalStyles,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  ShieldOutlined,
  WarningAmberRounded,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

const REMEMBERED_EMAIL_KEY = 'ibop_remembered_email';

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [capsLockOn, setCapsLockOn] = useState(false);
  const [error, setError] = useState('');
  const [sessionExpired, setSessionExpired] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [shakeKey, setShakeKey] = useState(0);

  const passwordRef = useRef<HTMLInputElement>(null);

  const from = (location.state as any)?.from?.pathname || '/';

  // On mount: restore remembered email + detect session expiry redirect
  useEffect(() => {
    const saved = localStorage.getItem(REMEMBERED_EMAIL_KEY);
    if (saved) {
      setEmail(saved);
      setRememberMe(true);
      // Email already filled — focus password immediately
      setTimeout(() => passwordRef.current?.focus(), 80);
    }

    const params = new URLSearchParams(window.location.search);
    if (params.get('reason') === 'expired') {
      setSessionExpired(true);
      // Clean URL without reload
      window.history.replaceState({}, '', '/login');
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();

    setError('');
    setSessionExpired(false);
    setIsLoading(true);

    try {
      const response = await login({ email, password });

      // Persist or clear remembered email
      if (rememberMe) {
        localStorage.setItem(REMEMBERED_EMAIL_KEY, email);
      } else {
        localStorage.removeItem(REMEMBERED_EMAIL_KEY);
      }

      await new Promise(resolve => setTimeout(resolve, 100));

      if (response.must_change_password) {
        navigate('/profile?force=1', { replace: true });
      } else {
        navigate(from, { replace: true });
      }
    } catch (err: any) {
      console.error('Login error:', err);
      // 423 = account locked — backend already returns Thai message with remaining minutes
      if (err.response?.status === 423 || err.response?.data?.detail) {
        setError(err.response.data.detail);
      } else if (err.response?.status === 401) {
        setError('อีเมลหรือรหัสผ่านไม่ถูกต้อง');
      } else {
        setError('เกิดข้อผิดพลาดในการเข้าสู่ระบบ กรุณาลองใหม่อีกครั้ง');
      }
      setShakeKey(k => k + 1);
      setIsLoading(false);
    }
  };

  const handlePasswordKeyEvent = (e: React.KeyboardEvent) => {
    setCapsLockOn(e.getModifierState('CapsLock'));
  };

  return (
    <>
      <GlobalStyles styles={`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          15%       { transform: translateX(-8px); }
          30%       { transform: translateX(7px); }
          45%       { transform: translateX(-6px); }
          60%       { transform: translateX(5px); }
          75%       { transform: translateX(-3px); }
          90%       { transform: translateX(2px); }
        }
        @keyframes fadeSlideIn {
          from { opacity: 0; transform: translateY(18px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes panelIn {
          from { opacity: 0; transform: translateX(-24px); }
          to   { opacity: 1; transform: translateX(0); }
        }
      `} />

      <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>

        {/* ── LEFT BRAND PANEL ── */}
        <Box
          sx={{
            display: { xs: 'none', md: 'flex' },
            flexDirection: 'column',
            justifyContent: 'space-between',
            width: '45%',
            minWidth: 320,
            bgcolor: '#1565c0',
            color: '#fff',
            px: 6,
            py: 5,
            position: 'relative',
            overflow: 'hidden',
            animation: 'panelIn 0.6s ease both',
          }}
        >
          {/* Geometric pattern overlay */}
          <Box
            aria-hidden="true"
            sx={{
              position: 'absolute',
              inset: 0,
              opacity: 0.07,
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
              pointerEvents: 'none',
            }}
          />

          {/* top wordmark */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, position: 'relative' }}>
            <ShieldOutlined sx={{ fontSize: 32, opacity: 0.9 }} />
            <Box>
              <Typography
                sx={{
                  fontFamily: '"Noto Sans Thai", sans-serif',
                  fontWeight: 700,
                  fontSize: '1rem',
                  letterSpacing: '0.1em',
                  opacity: 0.95,
                  textTransform: 'uppercase',
                  lineHeight: 1.2,
                }}
              >
                IBOP
              </Typography>
              <Typography
                sx={{
                  fontFamily: '"Noto Sans Thai", sans-serif',
                  fontWeight: 400,
                  fontSize: '0.65rem',
                  letterSpacing: '0.04em',
                  opacity: 0.8,
                  textTransform: 'uppercase',
                  lineHeight: 1.2,
                }}
              >
                Insurance Back Office Platform
              </Typography>
            </Box>
          </Box>

          {/* center copy */}
          <Box sx={{ position: 'relative' }}>
            <Typography
              sx={{
                fontFamily: '"Noto Sans Thai", sans-serif',
                fontWeight: 700,
                fontSize: { md: '2rem', lg: '2.4rem' },
                lineHeight: 1.35,
                mb: 2,
              }}
            >
              ระบบจัดการงาน<br />นายหน้าประกันภัย
            </Typography>
            <Box
              sx={{
                width: 48,
                height: 3,
                bgcolor: 'rgba(255,255,255,0.4)',
                borderRadius: 2,
                mb: 2.5,
              }}
            />
            <Typography
              sx={{
                fontFamily: '"Noto Sans Thai", sans-serif',
                fontWeight: 400,
                fontSize: '1.05rem',
                opacity: 0.9,
                lineHeight: 1.7,
              }}
            >
              บริหารกรมธรรม์ครบวงจร<br />
              ตั้งแต่ใบเสนอราคาถึงคอมมิชชั่น
            </Typography>
          </Box>

          {/* bottom version */}
          <Typography
            sx={{
              fontFamily: '"Noto Sans Thai", sans-serif',
              fontSize: '0.75rem',
              opacity: 0.45,
              position: 'relative',
            }}
          >
            Insurance Back Office Platform v1.0
          </Typography>
        </Box>

        {/* ── RIGHT FORM PANEL ── */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            bgcolor: '#f5f5f5',
            px: { xs: 3, sm: 6 },
            py: 4,
          }}
        >
          {/* Mobile header strip */}
          <Box
            sx={{
              display: { xs: 'flex', md: 'none' },
              alignItems: 'center',
              gap: 1.5,
              mb: 4,
            }}
          >
            <ShieldOutlined sx={{ fontSize: 28, color: '#1565c0' }} />
            <Box>
              <Typography
                sx={{
                  fontFamily: '"Noto Sans Thai", sans-serif',
                  fontWeight: 700,
                  fontSize: '1rem',
                  color: '#1565c0',
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  lineHeight: 1.2,
                }}
              >
                IBOP
              </Typography>
              <Typography
                sx={{
                  fontFamily: '"Noto Sans Thai", sans-serif',
                  fontWeight: 300,
                  fontSize: '0.6rem',
                  color: '#1565c0',
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                  opacity: 0.65,
                  lineHeight: 1.2,
                }}
              >
                Insurance Back Office Platform
              </Typography>
            </Box>
          </Box>

          {/* Form card */}
          <Box
            key={shakeKey}
            component="form"
            onSubmit={handleSubmit}
            noValidate
            sx={{
              width: '100%',
              maxWidth: 400,
              bgcolor: '#fff',
              borderRadius: 3,
              boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
              px: { xs: 3, sm: 4.5 },
              py: 5,
              animation: shakeKey === 0
                ? 'fadeSlideIn 0.5s ease both'
                : 'shake 0.5s ease both',
            }}
          >
            <Typography
              component="h1"
              sx={{
                fontFamily: '"Noto Sans Thai", sans-serif',
                fontWeight: 700,
                fontSize: '1.5rem',
                color: '#1a237e',
                mb: 0.5,
              }}
            >
              เข้าสู่ระบบ
            </Typography>
            <Typography
              sx={{
                fontFamily: '"Noto Sans Thai", sans-serif',
                fontSize: '0.875rem',
                color: 'text.secondary',
                mb: 3,
              }}
            >
              ระบบจัดการงานนายหน้าประกันภัย
            </Typography>

            {/* Session expired banner */}
            {sessionExpired && !error && (
              <Alert
                severity="warning"
                onClose={() => setSessionExpired(false)}
                sx={{ mb: 2.5, fontFamily: '"Noto Sans Thai", sans-serif' }}
              >
                เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่
              </Alert>
            )}

            {/* Wrong credentials error */}
            {error && (
              <Alert
                severity="error"
                onClose={() => setError('')}
                sx={{ mb: 2.5, fontFamily: '"Noto Sans Thai", sans-serif' }}
              >
                {error}
              </Alert>
            )}

            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="อีเมล"
              name="email"
              autoComplete="email"
              autoFocus={!rememberMe}
              value={email}
              onChange={(e) => { setEmail(e.target.value); setError(''); }}
              disabled={isLoading}
              error={!!error}
              sx={{ mb: 1 }}
            />

            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="รหัสผ่าน"
              type={showPassword ? 'text' : 'password'}
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => { setPassword(e.target.value); setError(''); }}
              onKeyDown={handlePasswordKeyEvent}
              onKeyUp={handlePasswordKeyEvent}
              disabled={isLoading}
              error={!!error}
              inputRef={passwordRef}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={showPassword ? 'ซ่อนรหัสผ่าน' : 'แสดงรหัสผ่าน'}
                      onClick={() => setShowPassword(v => !v)}
                      onMouseDown={(e) => e.preventDefault()}
                      edge="end"
                      size="small"
                      tabIndex={-1}
                    >
                      {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {/* Caps Lock warning */}
            {capsLockOn && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  mt: 0.75,
                  px: 1,
                }}
              >
                <WarningAmberRounded sx={{ fontSize: 15, color: 'warning.main' }} />
                <Typography
                  sx={{
                    fontFamily: '"Noto Sans Thai", sans-serif',
                    fontSize: '0.75rem',
                    color: 'warning.dark',
                  }}
                >
                  Caps Lock เปิดอยู่
                </Typography>
              </Box>
            )}

            {/* Remember me */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  size="small"
                  sx={{ color: '#1565c0', '&.Mui-checked': { color: '#1565c0' } }}
                  disabled={isLoading}
                />
              }
              label={
                <Typography
                  sx={{
                    fontFamily: '"Noto Sans Thai", sans-serif',
                    fontSize: '0.85rem',
                    color: 'text.secondary',
                  }}
                >
                  จดจำอีเมลนี้
                </Typography>
              }
              sx={{ mt: 1.5, ml: 0 }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={isLoading || !email || !password}
              sx={{
                mt: 2,
                py: 1.4,
                fontFamily: '"Noto Sans Thai", sans-serif',
                fontWeight: 600,
                fontSize: '1rem',
                bgcolor: '#1565c0',
                '&:hover': { bgcolor: '#0d47a1' },
                borderRadius: 2,
              }}
            >
              {isLoading ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : 'เข้าสู่ระบบ'}
            </Button>
          </Box>

          <Typography
            sx={{
              mt: 4,
              fontFamily: '"Noto Sans Thai", sans-serif',
              fontSize: '0.75rem',
              color: 'text.disabled',
            }}
          >
            Insurance Back Office Platform v1.0
          </Typography>
        </Box>
      </Box>
    </>
  );
};

export default LoginPage;
